import type React from "react";
import { memo } from "react";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const SEARCH_RESULT_HEADER_CLASS =
  "flex items-center justify-between px-1 py-1 animate-in fade-in slide-in-from-top-1 duration-200";
const SEARCH_RESULT_ROW_CLASS = "flex items-center gap-2 text-sm text-gray-600";
const SEARCH_RESULT_QUERY_CLASS = "font-medium";

export type SearchResultHeaderProps = {
  visible: boolean;
  queryText: string;
  resultLabel: string;
  resultCountLabel: string;
  noConditionText: string;
};

const SearchResultHeaderRaw: React.FC<SearchResultHeaderProps> = ({
  visible,
  queryText,
  resultLabel,
  resultCountLabel,
  noConditionText,
}) => {
  const { t } = useTranslation();

  if (!visible) return null;

  return (
    <div className={SEARCH_RESULT_HEADER_CLASS}>
      <div className={SEARCH_RESULT_ROW_CLASS}>
        <span className="text-yellow-500">💡</span>
        <span className={SEARCH_RESULT_QUERY_CLASS}>
          {`"${queryText || noConditionText}"`}
        </span>
        <span>{resultLabel}</span>
        <span className="font-bold">{resultCountLabel}</span>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="focus-visible:ring-rw-main/30 inline-flex size-5 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2"
                aria-label={t("streamSearch.resultCountExplanation")}
              >
                <Info className="size-3.5" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t("streamSearch.resultCountExplanation")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export const SearchResultHeader = memo(SearchResultHeaderRaw);
SearchResultHeader.displayName = "SearchResultHeader";
